import type { ProviderDetail } from '../../../../../js/api';
import { DocumentSections } from './DocumentSections';
import { LegacyUploadsNote } from './LegacyUploadsNote';
import { fileCountFor, ProviderDrawerMaps, providerFileName } from './providerDisplay';

/**
 * The education documents, in display order: the DocumentType slug that holds new
 * uploads, the `ProviderFileType` code of the LEGACY uploads of the same kind (files
 * only, on the old profile — counted here, never listed), and whether every clinician
 * is expected to have one (flags the upload bar and the Education tab label).
 */
export const EDUCATION_DOCUMENT_TYPES = [
  { slug: 'medical-diploma', legacyFileType: 'MEDICAL_DIPLOMA', required: true },
  { slug: 'internship', legacyFileType: 'INTERNSHIP', required: true },
  { slug: 'residency-certificate', legacyFileType: 'RESIDENCY_CERTIFICATE', required: true },
  { slug: 'fellowship-certificate', legacyFileType: 'FELLOWSHIP_CERTIFICATE', required: true },
  // not every clinician has these two
  { slug: 'ecfmg', legacyFileType: 'ECFMG', required: false },
  { slug: 'cme', legacyFileType: 'CME', required: false },
] as const;

/**
 * Education documents (languages live on General). Most clinicians going through
 * credentialing need at least one of each type, so the gaps are one click to fill:
 * the standard document sections — cards for the types on file, one upload bar for
 * the rest.
 */
export function EducationTab({
  provider,
  maps,
  onOpenDocuments,
}: {
  provider: ProviderDetail;
  maps: ProviderDrawerMaps;
  /** Switches the drawer to the Documents tab (legacy uploads live there). */
  onOpenDocuments: () => void;
}) {
  const legacyUploads = EDUCATION_DOCUMENT_TYPES.map(({ legacyFileType }) => ({
    label: maps.ProviderFileType?.[legacyFileType] || legacyFileType,
    count: fileCountFor(provider.file_counts, legacyFileType),
  })).filter((upload) => upload.count > 0);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4" data-testid="provider-education-documents">
      <DocumentSections
        providerId={provider.id}
        types={EDUCATION_DOCUMENT_TYPES}
        testIdPrefix="provider-education"
        zipSubject={`${providerFileName(provider)} Education Documents`}
        emptyText="No education documents on file."
      />
      {legacyUploads.length > 0 && (
        <LegacyUploadsNote
          summary={`Uploads not yet in Documents: ${legacyUploads
            .map((upload) => `${upload.label} (${upload.count})`)
            .join(', ')}.`}
          onOpenDocuments={onOpenDocuments}
          data-testid="provider-education-legacy"
        />
      )}
    </div>
  );
}
